import { modals } from '@mantine/modals'
import { useState } from 'react'

import { BENESTREAM_ORG_ID } from 'src/common/config'
import { appLocalLogo } from 'src/common/libs'
import { cleanStringToBeUrlReadable } from 'src/default/helpers/clean-string'
import { useTabSync } from 'src/v2/commons/hooks/useTabSync'
import { blockerManager } from 'src/v2/commons/services/blockerManager.service'
import {
  setNoteBlockerActive,
  setUserSession,
} from 'src/v2/domains/user/store/user.store'
import { useAppDispatch, useAppSelector } from 'src/v2/store/hooks'

import {
  clearPendingAuthenticatedLocation,
  getPendingAuthenticatedLocation,
} from '../utils/organizationNavigationSession.utils'
import { verifyVendorUserForBCF } from '../utils/verifyVendorUserForBCF'

import { navigateToApplication, useRouter } from '@v2/commons/utils/navigation'
import {
  authApiEndpoints,
  isTokenAuthResponse,
  useChangeOrganizationMutation,
} from '@v2/domains/auth/api/auth.api'
import { setAuthInfo } from '@v2/domains/auth/store/auth.store'
import { isCookieAuthenticationEnabled } from '@v2/domains/auth/utils/isCookieAuthenticationEnabled'
import {
  OrganizationSwitchSource,
  finishOrganizationSwitch,
  startOrganizationSwitch,
} from '@v2/domains/organization/store/organizationSwitch.store'
import { resetAccountStoreAccount } from '@v2/domains/person/store/account/assignAccount.store'
import { resetLeadStoreLead } from '@v2/domains/person/store/leads/assignLead.store'
import { useTheme } from '@v2/domains/ui/hooks/useTheme.hook'
import { setGlobalIsLoading } from '@v2/domains/ui/store/ui.store'
import { ThemeType } from '@v2/domains/ui/ui.types'
import { selectOrganizations } from '@v2/domains/user/selectors/user.selectors'
import { rootApi } from '@v2/store/api/rootApi'

export interface ChangeOrganizationParams {
  organizationId: string
  membershipId: string
  personId?: string
  responseRefreshToken?: string
  targetPath?: string
  source?: OrganizationSwitchSource
}

export const useChangeOrganizationProcess = () => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const refreshToken = useAppSelector((state) => state.auth.refreshToken)
  const organizations = useAppSelector(selectOrganizations)
  const [isChanging, setIsChanging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { loadTheme } = useTheme()

  const [changeOrganization] = useChangeOrganizationMutation()
  const { broadcast } = useTabSync()

  const broadcastSuccessfulOrganizationSwitch = ({
    organizationId,
    organizationName,
    source,
    targetPath,
  }: {
    organizationId: string
    organizationName: string
    source: OrganizationSwitchSource
    targetPath: string
  }) => {
    if (source === 'cross-tab' || source === 'login') {
      return
    }

    broadcast({
      type: 'ORG_SWITCH',
      organizationId,
      organizationName,
      targetPath,
    })
  }

  const resolveTargetPath = (
    organizationId: string,
    organizationName: string,
    membershipRoleId?: string,
    personId?: string,
    targetPath?: string,
  ) => {
    if (targetPath) {
      return targetPath
    }

    if (
      verifyVendorUserForBCF({
        organizationId,
        membershipId: membershipRoleId,
      })
    ) {
      return `/${cleanStringToBeUrlReadable(
        organizationName,
      )}/enrollment-checker`
    }

    if (personId) {
      return `/${cleanStringToBeUrlReadable(
        organizationName,
      )}/leads/${personId}`
    }

    return `/${cleanStringToBeUrlReadable(organizationName)}/leads`
  }

  const handleChangeOrganization = async ({
    organizationId,
    membershipId,
    personId,
    responseRefreshToken,
    targetPath,
    source = 'unknown',
  }: ChangeOrganizationParams) => {
    if (!organizationId || !membershipId) {
      setError('Organization ID and Membership ID are required')
      return
    }

    setIsChanging(true)
    dispatch(setGlobalIsLoading(true))
    dispatch(
      startOrganizationSwitch({
        targetOrganizationId: organizationId,
        targetPath,
        source,
      }),
    )
    setError(null)

    try {
      const refreshResult = await changeOrganization({
        organizationId,
        membershipId,
        ...(!isCookieAuthenticationEnabled && {
          refreshToken: refreshToken || responseRefreshToken || '',
          refresh_token: refreshToken || responseRefreshToken || '',
        }),
      }).unwrap()

      dispatch(
        setAuthInfo({
          userId: refreshResult.userId,
          organizationId,
          membershipId,
          ...(isTokenAuthResponse(refreshResult) && {
            access_token: refreshResult.access_token,
            refresh_token: refreshResult.refresh_token,
          }),
        }),
      )

      if (organizationId === BENESTREAM_ORG_ID) {
        const organizationName = Array.isArray(organizations)
          ? organizations.find(
              (organization) => organization.id === organizationId,
            )?.name || 'Benestream'
          : 'Benestream'
        const pendingAuthenticatedLocation =
          source === 'login'
            ? getPendingAuthenticatedLocation({
                id: organizationId,
                name: organizationName,
              })
            : null
        const resolvedTargetPath =
          targetPath ?? pendingAuthenticatedLocation ?? '/benestream/leads'

        broadcastSuccessfulOrganizationSwitch({
          organizationId,
          organizationName,
          source,
          targetPath: resolvedTargetPath,
        })

        blockerManager.resetAllBlockers()
        dispatch(setNoteBlockerActive(false))
        if (pendingAuthenticatedLocation === resolvedTargetPath) {
          clearPendingAuthenticatedLocation()
        }
        navigateToApplication(resolvedTargetPath)

        return
      }

      const profileResult = await dispatch(
        authApiEndpoints.getAuthProfile.initiate(null, {
          forceRefetch: true,
        }),
      ).unwrap()

      const organizationName = profileResult?.organization?.name
      const pendingAuthenticatedLocation =
        source === 'login'
          ? getPendingAuthenticatedLocation(profileResult.organization)
          : null
      const resolvedTargetPath =
        targetPath ??
        pendingAuthenticatedLocation ??
        resolveTargetPath(
          organizationId,
          organizationName,
          profileResult?.membership?.membershipRole?.id,
          personId,
        )

      appLocalLogo.setData('logo', profileResult.organization.circularLogoURL)

      dispatch(
        setUserSession({
          user: profileResult.user,
          currentOrganization: profileResult.organization,
          membership: profileResult.membership,
          policies: profileResult.policies,
        }),
      )

      dispatch(resetLeadStoreLead())
      dispatch(resetAccountStoreAccount())
      dispatch(rootApi.util.resetApiState())
      modals.closeAll()

      // Reset blockers before navigation to prevent deadlocks after switch.
      blockerManager.resetAllBlockers()
      dispatch(setNoteBlockerActive(false))

      loadTheme(ThemeType.IDG)

      broadcastSuccessfulOrganizationSwitch({
        organizationId,
        organizationName,
        source,
        targetPath: resolveTargetPath(
          organizationId,
          organizationName,
          profileResult?.membership?.membershipRole?.id,
        ),
      })

      router.push(resolvedTargetPath)
      if (pendingAuthenticatedLocation === resolvedTargetPath) {
        clearPendingAuthenticatedLocation()
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'An error occurred while changing organization'

      blockerManager.resetAllBlockers()
      dispatch(setNoteBlockerActive(false))
      setError(errorMessage)
      return {
        success: false,
        error: errorMessage,
      }
    } finally {
      dispatch(finishOrganizationSwitch())
      setIsChanging(false)
      dispatch(setGlobalIsLoading(false))
    }
  }

  return {
    handleChangeOrganization,
    isChanging,
    error,
  }
}
